/**
 * Read More Functionality
 * Handles expanding truncated content with permanent expansion
 * Supports character, word, and line-based truncation
 */

class ReadMore {
  constructor(wrapper, options = {}) {
    this.wrapper = wrapper;
    this.description = wrapper.querySelector('.collection__description, .read-more-content');
    this.toggle = wrapper.querySelector('.read-more-toggle');
    
    // Configuration options
    this.config = {
      type: 'lines', // 'characters', 'words', or 'lines'
      limit: 3, // number of lines/words/characters
      ...this.getDataAttributes(),
      ...options
    };
    
    this.originalContent = '';
    this.init();
  }

  getDataAttributes() {
    const config = {};
    if (this.wrapper.dataset.readMoreType) config.type = this.wrapper.dataset.readMoreType;
    if (this.wrapper.dataset.readMoreLimit) config.limit = parseInt(this.wrapper.dataset.readMoreLimit);
    return config;
  }

  init() {
    if (!this.description || !this.toggle) {
      console.log('ReadMore: Missing elements', {
        description: !!this.description,
        toggle: !!this.toggle,
        wrapper: this.wrapper
      });
      return;
    }
    
    // Store original content
    this.originalContent = this.description.innerHTML;
    
    // Apply truncation based on type
    this.applyTruncation();
    
    // Set up permanent expansion behavior
    this.setupToggle();
    
    console.log('ReadMore initialized:', {
      type: this.config.type,
      limit: this.config.limit,
      hasLongContent: this.wrapper.classList.contains('has-long-content')
    });
  }

  applyTruncation() {
    switch (this.config.type) {
      case 'characters':
        this.truncateByCharacters();
        break;
      case 'words':
        this.truncateByWords();
        break;
      case 'lines':
      default:
        this.truncateByLines();
        break;
    }
  }

  truncateByCharacters() {
    const text = this.description.textContent;
    if (text.length > this.config.limit) {
      const truncated = text.substring(0, this.config.limit);
      this.description.innerHTML = truncated + '...';
      this.wrapper.classList.add('has-long-content');
    }
  }

  truncateByWords() {
    const text = this.description.textContent;
    const words = text.split(' ');
    if (words.length > this.config.limit) {
      const truncated = words.slice(0, this.config.limit).join(' ');
      this.description.innerHTML = truncated + '...';
      this.wrapper.classList.add('has-long-content');
    }
  }

  truncateByLines() {
    // For line-based truncation, we use CSS and check overflow
    const lineHeight = parseFloat(window.getComputedStyle(this.description).lineHeight);
    const maxHeight = lineHeight * this.config.limit;
    
    // Set dynamic max-height
    this.description.style.setProperty('--read-more-max-height', `${maxHeight}px`);
    this.description.style.maxHeight = 'var(--read-more-max-height)';
    
    // Check if content overflows
    if (this.description.scrollHeight > maxHeight) {
      this.wrapper.classList.add('has-long-content');
    }
  }

  setupToggle() {
    this.toggle.addEventListener('change', () => {
      console.log('ReadMore toggle clicked:', this.toggle.checked);
      
      if (this.toggle.checked) {
        // Restore original content if it was truncated
        if (this.config.type === 'characters' || this.config.type === 'words') {
          this.description.innerHTML = this.originalContent;
          console.log('Content restored');
        }
        
        // For line-based truncation, remove the max-height constraint
        if (this.config.type === 'lines') {
          this.description.style.maxHeight = 'none';
          this.description.style.removeProperty('--read-more-max-height');
          console.log('Line height constraint removed');
        }
        
        // Add expanded class and update button text
        this.wrapper.classList.add('expanded');
        this.updateButtonText(true);
        
        console.log('ReadMore expanded');
      } else {
        // Collapse content back
        if (this.config.type === 'characters' || this.config.type === 'words') {
          this.applyTruncation();
          console.log('Content truncated');
        }
        
        // For line-based truncation, restore the max-height constraint
        if (this.config.type === 'lines') {
          this.applyTruncation();
          console.log('Line height constraint restored');
        }
        
        // Remove expanded class and update button text
        this.wrapper.classList.remove('expanded');
        this.updateButtonText(false);
        
        console.log('ReadMore collapsed');
      }
    });
  }

  updateButtonText(isExpanded) {
    const buttonText = this.wrapper.querySelector('.read-more-text');
    if (buttonText) {
      const readMoreText = this.wrapper.dataset.readMoreText || 'Read more';
      const readLessText = this.wrapper.dataset.readLessText || 'Read less';
      buttonText.textContent = isExpanded ? readLessText : readMoreText;
    }
  }
}

// Initialize read-more functionality
function initReadMore() {
  const wrappers = document.querySelectorAll('.collection__description-wrapper, .read-more-wrapper');
  
  wrappers.forEach(wrapper => {
    new ReadMore(wrapper);
  });
}

// Auto-initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initReadMore);
} else {
  initReadMore();
}

// Export for manual initialization if needed
window.ReadMore = ReadMore;
window.initReadMore = initReadMore;
